import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createLightsSession,
    deleteLightsSession,
    moveLightsSession,
} from './lightsGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'lights_out';
const { UPLOAD_URL } = ARCADE.lights_out;
const ACCENT = GAME_ACCENT.lights_out;
const GRID_SIZE = 5;
const HINT = 'Arrows / WASD move · Enter / Space toggle · Esc pause';

// Re-exported for existing tests that import scoring constants from this module.
export {
    LIGHT_OFF_POINTS,
    SOLVE_BONUS,
    EFFICIENCY_POINTS_PER_MOVE,
    EFFICIENCY_MOVE_BUDGET,
} from './lightsGmClient';

const emptyBoard = () => Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(0));

const LightsOut = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [board, setBoard] = useState(emptyBoard);
    const [moves, setMoves] = useState(0);
    const [score, setScore] = useState(0);
    const [solved, setSolved] = useState(false);
    const [cursor, setCursor] = useState({ row: 2, col: 2 });
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const applyGm = useCallback((data) => {
        if (Array.isArray(data.board)) setBoard(data.board.map((r) => r.slice()));
        if (typeof data.moves === 'number') setMoves(data.moves);
        if (typeof data.score === 'number') setScore(data.score);
        setSolved(Boolean(data.is_solved));
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over || data.is_solved || data.game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            moves: typeof data.moves === 'number' ? data.moves : 0,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteLightsSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createLightsSession();
        if (!data?.session_id) throw new Error('Lights GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setCursor({ row: 2, col: 2 });
        const initBoard = data.init_state?.board || data.board;
        recorder.reset({
            init_state: { board: Array.isArray(initBoard) ? initBoard.map((r) => r.slice()) : emptyBoard() },
        });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteLightsSession(sid);
        play.endGame({ score });
        setBoard(emptyBoard());
        setMoves(0);
        setScore(0);
        setSolved(false);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteLightsSession(sid);
    }, []);

    const press = useCallback(async (row, col) => {
        if (play.phase !== 'playing' || solved || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveLightsSession(sid, row, col);
            const meta = applyGm(data);
            const last = Array.isArray(data.history) ? data.history[data.history.length - 1] : null;
            recorder.recordTick({
                row,
                col,
                board: last?.board || data.board,
                score: meta.score,
                moves: meta.moves,
                lights_extinguished: last?.lights_extinguished,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({ score: meta.score, extra: { moves: meta.moves } });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteLightsSession(done);
            }
        } catch (err) {
            console.warn('Lights GM move failed', err);
            setMessage(err?.data?.message || 'Move failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, play, recorder, solved, setMessage]);

    const onKey = useCallback((e) => {
        if (['ArrowUp', 'w', 'W'].includes(e.key)) {
            setCursor((c) => ({ ...c, row: Math.max(0, c.row - 1) }));
            e.preventDefault();
        } else if (['ArrowDown', 's', 'S'].includes(e.key)) {
            setCursor((c) => ({ ...c, row: Math.min(GRID_SIZE - 1, c.row + 1) }));
            e.preventDefault();
        } else if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
            setCursor((c) => ({ ...c, col: Math.max(0, c.col - 1) }));
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
            setCursor((c) => ({ ...c, col: Math.min(GRID_SIZE - 1, c.col + 1) }));
            e.preventDefault();
        } else if (e.key === 'Enter' || e.key === ' ') {
            press(cursor.row, cursor.col);
            e.preventDefault();
        }
    }, [cursor, press]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [cursor, press],
    });

    return (
        <ArcadeGameShell
            title="Lights Out"
            titleAccent="Lab"
            subtitle="Toggle tiles until every light is off"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.lights_out}
            session={session}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Moves: ${moves}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={solved ? 'Solved' : 'In Progress'} size="small" color={solved ? 'success' : 'warning'} />
                    </Stack>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
                        gap: 1.2, p: 2, maxWidth: 360, mx: 'auto', borderRadius: 3,
                        border: '2px solid rgba(255,255,255,0.12)',
                    }}>
                        {board.map((row, rIdx) => row.map((value, cIdx) => {
                            const focused = play.isActive && cursor.row === rIdx && cursor.col === cIdx;
                            return (
                                <Box
                                    key={`${rIdx}-${cIdx}`}
                                    onClick={() => {
                                        if (play.phase !== 'playing') return;
                                        setCursor({ row: rIdx, col: cIdx });
                                        press(rIdx, cIdx);
                                    }}
                                    sx={{
                                        aspectRatio: '1', borderRadius: 2,
                                        background: value ? 'linear-gradient(180deg, #FFE066, #FFC300)' : 'rgba(255,255,255,0.08)',
                                        boxShadow: value ? '0 0 18px rgba(255,224,102,0.45)' : 'inset 0 0 8px rgba(0,0,0,0.4)',
                                        cursor: solved || play.phase !== 'playing' ? 'default' : 'pointer',
                                        border: focused ? `2px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.15)',
                                        outline: focused ? `2px solid ${ACCENT}` : 'none',
                                    }}
                                />
                            );
                        }))}
                    </Box>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default LightsOut;
